import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth, loginWithGoogle } from "../lib/AuthContext";
import { useCart } from "../lib/CartContext";
import { useSettings } from "../lib/SettingsContext";

const NAV = [
  { to: "/", label: "Acasă" },
  { to: "/shop", label: "Magazin" },
  { to: "/shop?rare=true", label: "Rarități" },
  { to: "/about", label: "Despre Noi" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const { settings } = useSettings();
  const brandName = settings.brand_name || "Johny's Antique Shop";
  const brandLogo = settings.brand_logo || "";
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/shop?search=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setQ("");
    }
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "glass" : "bg-transparent"}`} data-testid="navbar">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            {brandLogo ? (
              <img src={brandLogo} alt={brandName} className="h-12 w-12 object-contain" />
            ) : (
              <div className="w-9 h-9 border border-[#D4AF37] flex items-center justify-center">
                <span className="font-serif text-[#D4AF37] text-lg">J</span>
              </div>
            )}
            <div className="leading-tight">
              <div className="font-serif text-lg sm:text-xl tracking-wide text-[#F8F4E6]">{brandName}</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {NAV.map(n => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `text-[12px] uppercase tracking-[0.25em] font-light transition-colors ${isActive ? "text-[#D4AF37]" : "text-[#A39E8A] hover:text-[#F8F4E6]"}`
                }
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,"-")}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-5">
            <button onClick={() => setSearchOpen(true)} aria-label="Caută" className="text-[#A39E8A] hover:text-[#D4AF37] transition" data-testid="search-button">
              <Search size={18} strokeWidth={1.2} />
            </button>
            <Link to="/wishlist" aria-label="Wishlist" className="text-[#A39E8A] hover:text-[#D4AF37] transition hidden sm:block" data-testid="wishlist-icon">
              <Heart size={18} strokeWidth={1.2} />
            </Link>
            <button onClick={() => setOpen(true)} aria-label="Coș" className="relative text-[#A39E8A] hover:text-[#D4AF37] transition" data-testid="cart-button">
              <ShoppingBag size={18} strokeWidth={1.2} />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#D4AF37] text-black text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-medium">{count}</span>
              )}
            </button>
            {user ? (
              <div className="hidden sm:flex items-center gap-4">
                <Link to="/account" className="text-[#A39E8A] hover:text-[#D4AF37] transition" data-testid="account-link">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full border border-[#D4AF37]/40" />
                  ) : <User size={18} strokeWidth={1.2} />}
                </Link>
                {user.is_admin && (
                  <Link to="/admin" className="text-[#A39E8A] hover:text-[#D4AF37] transition" data-testid="admin-link">
                    <LayoutDashboard size={18} strokeWidth={1.2} />
                  </Link>
                )}
                <button onClick={logout} className="text-[#A39E8A] hover:text-[#D4AF37] transition" aria-label="Ieși" data-testid="logout-button">
                  <LogOut size={18} strokeWidth={1.2} />
                </button>
              </div>
            ) : (
              <button onClick={loginWithGoogle} className="hidden sm:inline-flex text-[11px] tracking-[0.2em] uppercase text-[#D4AF37] border-b border-[#D4AF37]/40 pb-0.5 hover:border-[#D4AF37]" data-testid="login-button">
                Conectare
              </button>
            )}
            <button className="lg:hidden text-[#A39E8A]" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] bg-[#050505] flex flex-col p-8" data-testid="mobile-menu">
          <div className="flex justify-between items-center mb-12">
            <span className="font-serif text-2xl text-[#D4AF37]">{brandName}</span>
            <button onClick={() => setMobileOpen(false)} className="text-[#F8F4E6]"><X /></button>
          </div>
          <nav className="flex flex-col gap-7">
            {NAV.map(n => (
              <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)} className="font-serif text-3xl text-[#F8F4E6]">{n.label}</Link>
            ))}
            <Link to="/wishlist" onClick={() => setMobileOpen(false)} className="font-serif text-3xl text-[#F8F4E6]">Lista de Dorințe</Link>
            {user ? <button onClick={() => { logout(); setMobileOpen(false); }} className="font-serif text-3xl text-[#D4AF37] text-left">Deconectare</button>
                  : <button onClick={() => { loginWithGoogle(); }} className="font-serif text-3xl text-[#D4AF37] text-left">Conectare</button>}
          </nav>
        </div>
      )}

      {/* Search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-[60] bg-[#050505]/95 backdrop-blur-2xl flex items-start justify-center pt-32 px-6" onClick={() => setSearchOpen(false)} data-testid="search-overlay">
          <form onSubmit={submitSearch} className="w-full max-w-2xl" onClick={e => e.stopPropagation()}>
            <p className="label-luxe mb-6">Caută în Colecție</p>
            <input
              autoFocus
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Ceasuri, picturi, mobilier..."
              className="w-full bg-transparent text-[#F8F4E6] font-serif text-4xl border-b border-[#D4AF37]/30 pb-4 outline-none focus:border-[#D4AF37]"
              data-testid="search-input"
            />
            <button type="submit" className="btn-gold mt-8" data-testid="search-submit">Caută</button>
          </form>
        </div>
      )}
    </>
  );
}
